import React from 'react';
import { useFetcher } from 'react-router-dom';
import {
  ArchiveBoxIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import type { ActionResult } from '@oysterjs/core/graphql/actions';

import { Action, type EmailTemplateListFieldsFragment, Resource } from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import type { RowAction } from '~/components/row-actions-menu';
import { actionFailureMessage } from './action-failure';
import { emailTemplatePath } from './email-template-paths';

type EmailTemplate = EmailTemplateListFieldsFragment;
type EmailTemplateAction = 'duplicate' | 'archive';

/**
 * "<name> (copy)", or "<name> (copy 2)" and up when that's taken. Names are compared
 * ignoring case, as the server does against the templates the user can see.
 */
export const nextCopyName = (name: string, takenNames: string[]): string => {
  const taken = new Set(takenNames.map((takenName) => takenName.toLowerCase()));
  const copyName = (copyNumber: number) =>
    copyNumber === 1 ? `${name} (copy)` : `${name} (copy ${copyNumber})`;
  let copyNumber = 1;
  while (taken.has(copyName(copyNumber).toLowerCase())) copyNumber++;
  return copyName(copyNumber);
};

type ArchiveDialogState = { template: EmailTemplate; isConfirmed: boolean };

/**
 * The row menu for the templates list. Archive asks first and has its own fetcher, so its
 * dialog can show the archive's progress and failure apart from Duplicate's.
 */
export const useEmailTemplateActions = (emailTemplates: EmailTemplate[]) => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(Resource.EmailTemplate, Action.Create);

  const duplicateFetcher = useFetcher<ActionResult<unknown>>();
  const archiveFetcher = useFetcher<ActionResult<unknown>>();
  const isDuplicating = duplicateFetcher.state !== 'idle';
  const isArchiving = archiveFetcher.state !== 'idle';
  const [archiveDialog, setArchiveDialog] = React.useState<ArchiveDialogState | null>(null);

  // A failed Duplicate stays on screen until the next action succeeds. A later Duplicate
  // replaces its result; a later Archive marks it as dealt with.
  const [dealtWithDuplicateResult, setDealtWithDuplicateResult] = React.useState<unknown>();
  const archiveResult = isArchiving ? undefined : archiveFetcher.data;
  const hasArchiveSucceeded = !!archiveResult && !actionFailureMessage(archiveResult);
  React.useEffect(() => {
    if (hasArchiveSucceeded) setDealtWithDuplicateResult(duplicateFetcher.data);
  }, [archiveResult]);
  const isDuplicateResultDealtWith = duplicateFetcher.data === dealtWithDuplicateResult;

  const actionPath = (template: EmailTemplate, action: EmailTemplateAction) =>
    `${emailTemplatePath(template.id)}/${action}`;
  const submitOptions = { method: 'post', encType: 'application/json' } as const;

  const templateNames = emailTemplates.map((template) => template.name);
  const canChooseVisibility = useVisibilityEntitlement();
  const duplicate = (template: EmailTemplate) =>
    duplicateFetcher.submit(
      { copyName: nextCopyName(template.name, templateNames), canChooseVisibility },
      { ...submitOptions, action: actionPath(template, 'duplicate') }
    );

  const actionsFor = (template: EmailTemplate): RowAction[] => [
    ...(template.canEdit
      ? [{ label: 'Edit', icon: PencilSquareIcon, href: emailTemplatePath(template.id) }]
      : []),
    ...(canCreate
      ? [{ label: 'Duplicate', icon: DocumentDuplicateIcon, onSelect: () => duplicate(template) }]
      : []),
    ...(template.canEdit
      ? [
          {
            label: 'Archive',
            icon: ArchiveBoxIcon,
            onSelect: () => setArchiveDialog({ template, isConfirmed: false })
          }
        ]
      : [])
  ];

  // An archived template drops out of the reloaded list, which closes the dialog. One that
  // failed to archive stays listed, so the dialog stays open to say why.
  const isArchiveTemplateListed =
    !!archiveDialog && emailTemplates.some((template) => template.id === archiveDialog.template.id);
  const showsArchiveFailure = !!archiveDialog?.isConfirmed && !isArchiving;

  return {
    actionsFor,
    isBusy: isDuplicating || isArchiving,
    failure:
      isDuplicating || isDuplicateResultDealtWith
        ? null
        : actionFailureMessage(duplicateFetcher.data),
    archive: {
      template: isArchiveTemplateListed ? archiveDialog.template : null,
      isArchiving,
      failure: showsArchiveFailure ? actionFailureMessage(archiveFetcher.data) : null,
      confirm: () => {
        if (!archiveDialog) return;
        setArchiveDialog({ ...archiveDialog, isConfirmed: true });
        archiveFetcher.submit(
          {},
          { ...submitOptions, action: actionPath(archiveDialog.template, 'archive') }
        );
      },
      cancel: () => setArchiveDialog(null)
    }
  };
};
